import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../_shared";
import { Button } from "@/components/ui/button";
import { KiriCharacter } from "@/components/kiri/KiriCharacter";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Sparkles } from "lucide-react";

export default function OnboardingStart() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [active, setActive] = useState(null);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    api.get("/guided-onboarding/sessions/active")
      .then(({ data }) => { setActive(data.session); setEnabled(data.settings?.onboarding_enabled !== false); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/guided-onboarding/sessions");
      nav(`/app/onboarding/session/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't start onboarding");
      setStarting(false);
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-zinc-500 text-xs uppercase tracking-widest font-mono">Loading…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="onboarding-start-page">
      <PageHeader title="Fieldlink Guided Onboarding" subtitle="Powered by Kiri — your Fieldlink onboarding guide." />

      {!enabled ? (
        <div className="mt-8 rounded-xl border border-white/10 bg-[#12161C] p-8 text-center" data-testid="onboarding-disabled-notice">
          <p className="text-zinc-300">Guided Onboarding is currently turned off for your account. Reach out via Support if you'd like a hand setting up.</p>
          <Button variant="outline" className="mt-4 rounded-lg" onClick={() => nav("/app/support")} data-testid="onboarding-go-support-btn">Contact Support</Button>
        </div>
      ) : active ? (
        <div className="mt-8 rounded-xl border border-[#FF5F15]/30 bg-[#12161C] p-8" data-testid="onboarding-resume-card">
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#FF5F15] font-bold">Welcome back</p>
          <h2 className="text-2xl font-black text-white mt-1">Your Fieldlink setup is {active.progress_pct}% complete</h2>
          <p className="text-zinc-400 mt-2 text-sm">
            Next up: {active.plan.find((s) => s.id === active.current_step_id)?.label || "Wrap up"}
          </p>
          <div className="w-full h-2 rounded-full bg-white/10 mt-4 overflow-hidden">
            <div className="h-full bg-[#FF5F15] rounded-full transition-all" style={{ width: `${active.progress_pct}%` }} />
          </div>
          <Button onClick={() => nav(`/app/onboarding/session/${active.id}`)} className="mt-6 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white font-bold uppercase tracking-wider" data-testid="onboarding-continue-btn">
            Continue with Kiri <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      ) : (
        <div className="mt-8 rounded-xl border border-white/10 bg-[#12161C] p-8 text-center" data-testid="onboarding-intro-card">
          <KiriCharacter useHero size="xl" state="idle" className="mx-auto" />
          <h2 className="text-2xl font-black text-white mt-4">Kia ora — I'm Kiri</h2>
          <p className="text-zinc-400 mt-2 max-w-md mx-auto text-sm">
            Tell me a bit about your business and I'll tailor a setup plan to it — talk it through, ask me
            anything as we go, and I'll show you exactly where things live in Fieldlink.
          </p>
          <Button onClick={start} disabled={starting} className="mt-6 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white font-bold uppercase tracking-wider" data-testid="onboarding-start-btn">
            <Sparkles className="w-4 h-4 mr-2" /> {starting ? "Starting…" : "Start Guided Onboarding"}
          </Button>
        </div>
      )}
    </div>
  );
}
